/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useState } from 'react';

import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Router } from '@uhg-abyss/web/ui/Router';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';

import { downloadFile } from '@src/store/services/cdb/onlineServices';

import { FileContext } from '@src/routes/Routes';

const cdbReportTypes = [
  {
    value: 'procStat',
    download: false,
    selected: true,
    label: 'Processing Statistics',
  },
  { value: 'ADD', reportType: 'ADD', label: 'Add Transactions' },
  {
    value: 'ENW',
    download: false,
    reportType: 'ENW',
    label: 'Error & Warning Summary',
  },
  { value: 'AT', reportType: 'AT', label: 'All Transactions' },
  {
    value: 'CHG',
    reportType: 'CHG',
    label: 'Change Transactions',
  },
  {
    value: 'EE',
    reportType: 'EE',
    label: 'Customer Corrections Required',
  },
  {
    value: 'IE',
    reportType: 'IE',
    label: 'Eligibility Analyst Corrections Required',
  },
  {
    value: 'PCPI',
    reportType: 'PCPI',
    label: 'PCP Error Transactions',
  },
  {
    value: 'PROR',
    reportType: 'PROR',
    label: 'PCP Rollover Report',
  },
  {
    value: 'NCH',
    reportType: 'NCH',
    label: 'No Change Transactions',
  },
  {
    value: 'TRM',
    reportType: 'TRM',
    label: 'Terms Submitted/ Auto-Cancel Transactions',
  },
  {
    value: 'FCH',
    reportType: 'FCH',
    label: 'Field Change Summary',
  },
];

const gpsReportTypes = [
  {
    value: 'procStat',
    download: false,
    selected: true,
    label: 'Processing Statistics',
  },
  { value: 'ADD', reportType: 'ADD', label: 'Add Transactions' },
  {
    value: 'ENW',
    download: false,
    reportType: 'ENW',
    label: 'Error & Warning Summary',
  },
  { value: 'AT', reportType: 'AT', label: 'All Transactions' },
  {
    value: 'CHG',
    reportType: 'CHG',
    label: 'Change Transactions',
  },
  {
    value: 'EE',
    reportType: 'EE',
    label: 'Customer Corrections Required',
  },
  {
    value: 'IE',
    reportType: 'IE',
    label: 'Eligibility Analyst Corrections Required',
  },
  {
    value: 'GFB',
    reportType: 'GFB',
    label: 'Pending GPS Status Update',
  },
  {
    value: 'NCH',
    reportType: 'NCH',
    label: 'No Change Transactions',
  },
  {
    value: 'TRM',
    reportType: 'TRM',
    label: 'Terms Submitted/ Auto-Cancel Transactions',
  },
  {
    value: 'FCH',
    reportType: 'FCH',
    label: 'Field Change Summary',
  },
];

const thresholdReports = [
  'procStat',
  'ENW',
  'AT',
  'PCPI',
  'PROR',
  'FCH',
  'GFB',
];

export function formatDate(inputDate) {
  const date = new Date(inputDate);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  return `${year}${month}${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * Header
 *
 * @returns {JSX.Element}
 * @constructor
 */
export const FilterAndDownload = ({
  fileId,
  setId,
  reportType,
  fileDetail,
  onChange,
  isChild = false,
}) => {
  const router = useRouter();
  const [reports, setReports] = useState<string[]>([]);
  const { fileData, setFileData } = useContext(FileContext);
  const [isChecked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { service } = router.getRouteParams();

  const reportTypes = service === 'gps' ? gpsReportTypes : cdbReportTypes;

  useEffect(() => {
    setChecked(false);
    setFileData((prevFileData) => {
      return {
        ...prevFileData,
        allDate: 'false',
      };
    });
  }, [reportType]);

  const isThresholdExceeded =
    fileDetail?.[0].fileStatus.includes('THRESHOLD_EXCEEDED');

  const filteredReportTypes = reportTypes.filter((r) => {
    return isThresholdExceeded ? thresholdReports.includes(r.value) : true;
  });

  const downloadReports = reportTypes.filter((r) => {
    if (r.download === false) return false;
    if (service === 'gps' && isThresholdExceeded) {
      return thresholdReports.includes(r.value);
    }
    return true;
  });

  const handleReportChange = (value) => {
    onChange?.(value);
  };

  const handleReportsChange = (value: string[] | number[]) => {
    setReports(value.map((item) => String(item)));
  };

  const handleDate = () => {
    setChecked((prev) => {
      return !prev;
    });
    setFileData((prevFileData) => {
      return {
        ...prevFileData,
        allDate: prevFileData.allDate === 'true' ? 'false' : 'true',
      };
    });
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    reports.forEach(async (report) => {
      const resolvedReport = reportTypes.find((x) => {
        return x.value === report;
      });
      let params;
      let body;
      if (service === 'gps') {
        params = {
          rptType: resolvedReport?.reportType || '',
          sbmtGrpId: fileData?.sbmtGrpId || '',
          fileId: fileDetail?.[0]?.fileId || '',
          flProcDttm: formatDate(
            fileData?.processingDt || fileDetail?.[0]?.processingDt || ''
          ),
          flRecvDttm: fileData?.fileRecvDate
            ? formatDate(fileData?.fileRecvDate || '')
            : fileDetail?.[0]?.fileRecvDate
            ? formatDate(fileDetail?.[0]?.fileRecvDate || '')
            : '',
          fileInd: '1',
          sbmtGrpNm: fileDetail?.[0]?.sbmtGrpName || '',
          lstNmSrch: '',
          empId: '',
          svrtyCd: '0',
          errTyp: 'string',
          fieldCd: '0',
        };

        downloadFile(params, 'gpsservice/gpsDownload', setIsLoading);
      } else {
        body = {
          rptType: resolvedReport?.reportType || '',
          sbmtGrpId: fileData?.sbmtGrpId || '',
          fileId: fileDetail?.[0]?.fileId.toString(),
          transSetId: fileDetail?.[0]?.transSetId.toString() || '',
          flProcDttm: formatDate(
            fileData?.processingDt || fileDetail?.[0]?.processingDt || ''
          ),
          flRecvDttm: formatDate(fileDetail?.[0]?.fileRecvDate || ''),
          fileInd: '1',
          sbmtGrpNm: fileDetail?.[0]?.sbmtGrpName || '',
          subscriberId: fileDetail?.[0]?.profType || '',
          profType: fileDetail?.[0]?.profType || '',
        };

        downloadFile(body, 'cdbservice/cdbParentDownload', setIsLoading);
      }
    });
  };

  const reportTypeLabel =
    reportTypes?.find((x) => {
      return x.value === reportType;
    })?.label || 'Report';

  return (
    <Grid>
      <Grid.Col span={{ xs: '50%', md: '50%', lg: '50%', xl: '50%' }}>
        <Layout.Group
          alignLayout="left"
          css={{
            label: {
              fontWeight: 'bold',
            },
          }}
        >
          {reportTypeLabel && <Router.MetaTags title={reportTypeLabel} />}
          <label style={{ width: '110px' }}>Select Report</label>
          <SelectInput
            label="Select Report"
            hideLabel
            width="350px"
            isClearable
            options={filteredReportTypes}
            value={reportType}
            isRequired
            onChange={handleReportChange}
            css={{
              'abyss-select-input-container': {
                fontSize: '14px !important',
              },
            }}
          />
          {reportType === 'IE' ||
          reportType === 'ADD' ||
          reportType === 'EE' ||
          reportType === 'NCH' ||
          reportType === 'TRM' ||
          reportType === 'CHG' ? (
            <Checkbox
              isChecked={isChecked}
              label="For all Dates"
              onChange={handleDate}
              css={{
                'abyss-checkbox-root': {
                  width: '130px',
                },
              }}
            />
          ) : null}
        </Layout.Group>
      </Grid.Col>
      <Grid.Col span={{ xs: '50%', md: '50%', lg: '50%', xl: '50%' }}>
        <Layout.Group alignLayout="right">
          <SelectInput
            isMulti
            label="Select One or More Reports"
            hideLabel
            isSearchable
            width="250px"
            value={reports}
            onChange={handleReportsChange}
            options={downloadReports}
            css={{
              'abyss-select-input-container': {
                fontSize: '14px !important',
              },
              'abyss-select-input-selection-container': {
                fontSize: '14px !important',
              },
            }}
          />
          <Button
            onClick={handleDownload}
            isLoading={isLoading}
            ariaLoadingLabel="Importing data"
            variant="outline"
            className="no-margin-left no-padding-left no-padding-right"
          >
            <strong>Download</strong>
          </Button>
        </Layout.Group>
      </Grid.Col>
    </Grid>
  );
};
